import { COOKIE_NAMES, DATE_FILTERS, SUBSCRIPTION_INTERVAL } from "../constants/constants";
import { User } from "../models/abacConfig";
import { deleteCookie, getCookie, isCookieValid, setCookieWithExpiration, updateCookieExpiration, updateCookieValue } from "./cookieManager";

export function updateUserContext(roles: any, permissions: any, userId: string, partyName: string, status: string, orgPartyRole: string,
    orgPartyIds: string[], orgPartyName: string, authorizationToken: string, newLogin: boolean, partySubscription: any = {}) {
    let expirationTime = new Date(Date.now() + 15 * 60 * 1000);
    const loggedInUser: User = {};
    if (newLogin) {
        loggedInUser.partyId = userId;
        loggedInUser.roles = roles;
        loggedInUser.permissions = permissions;
        loggedInUser.status = status;
        loggedInUser.orgPartyRole = orgPartyRole;
        loggedInUser.orgPartyIds = orgPartyIds;
        loggedInUser.orgPartyName = orgPartyName;
        loggedInUser.partyName = partyName;
        expirationTime = new Date(Date.now() + 90 * 60 * 1000);
        // console.log("expirationTime : ", expirationTime);
        const userObject = JSON.stringify(loggedInUser);
        setSessionVariable(COOKIE_NAMES.prismUser, userObject, expirationTime);
        setSessionVariable(COOKIE_NAMES.jwtToken, authorizationToken, expirationTime);
        setSessionVariable(COOKIE_NAMES.tokenExpiryTime, expirationTime.getTime(), expirationTime);
        
    } else {
        // For token refresh scenarios, extend the session by 2 hours if we have a new token
        // Otherwise, extend by 15 minutes for regular activity
        const isTokenRefresh = authorizationToken && authorizationToken !== "";
        const extensionTime = isTokenRefresh ? 2 * 60 * 60 * 1000 : 15 * 60 * 1000; // 2 hours for refresh, 15 min for activity
        const expirationTime = new Date(Date.now() + extensionTime);
        
        updateSessionVariableExpiration(COOKIE_NAMES.prismUser, expirationTime);
        if (authorizationToken && authorizationToken !== "") {
            updateSessionVariableValue(COOKIE_NAMES.jwtToken, authorizationToken, expirationTime);
            console.log('Token refreshed - extending session by 2 hours');
        } else {
            updateSessionVariableExpiration(COOKIE_NAMES.jwtToken, expirationTime);
            console.log('User activity - extending session by 15 minutes');
        }
        updateSessionVariableValue(COOKIE_NAMES.tokenExpiryTime, expirationTime.getTime(), expirationTime);
    }

    if (partySubscription) {
        console.log("partySubscription : ", partySubscription);
        setSessionVariable(COOKIE_NAMES.subscription, JSON.stringify(partySubscription), expirationTime);
    }
}

// Date format - 2024-02-15T00:00:00.000Z
export function convertMongoDate(dateString: any) {
    // Parse the given date string into a Date object
    if (!dateString || dateString === "") {
        return "";
    }
    const dateObject = new Date(dateString);
    // Format the date into DD/MM/YYYY format

    const formattedDate =
        `${dateObject.getFullYear()}-` +
        `${String(dateObject.getMonth() + 1).padStart(2, '0')}-` +
        `${String(dateObject.getDate()).padStart(2, '0')}`
        ;


    return formattedDate
}

export function getSessionVariable(variableName: any) {
    return getCookie(variableName);
}

export function setSessionVariable(variableName: any, variableValue: any, expirationTimeinMin: any) {
    setCookieWithExpiration(variableName, variableValue, expirationTimeinMin);
}

export function updateSessionVariableExpiration(variableName: any, extendedExpirationTimeinMin: any) {
    updateCookieExpiration(variableName, { expires: extendedExpirationTimeinMin });
}

export function updateSessionVariableValue(variableName: any, updatedValue: any, extendedExpirationTimeinMin: any) {
    updateCookieValue(variableName, updatedValue, { expires: extendedExpirationTimeinMin });
}

export function isSessionValid(variableName: any) {
    return isCookieValid(variableName);
}

export function clearSession(variableName: any) {
    return deleteCookie(variableName);
}

export function getUserContext() {
    let userContext = "";
    const prismUser = getSessionVariable(COOKIE_NAMES.prismUser);
    if (prismUser && prismUser != "") {
        userContext = JSON.parse(prismUser);
    }
    return userContext;
}

function getMonday(date: any) {
    let day = date.getDay(),
        diff = date.getDate() - day + (day == 0 ? -6 : 1); // adjust when day is sunday
    return new Date(date.setDate(diff));
}

function getSunday(date: any) {
    let day = date.getDay(),
        diff = date.getDate() + (7 - day) - (day == 0 ? 7 : 0); // adjust when day is sunday
    return new Date(date.setDate(diff));
}

export const getDates = (dateSpan: any) => {
    let startDate: any = "";
    let endDate: any = "";
    const present = new Date();
    switch (dateSpan?.type) {
        case DATE_FILTERS["TODAY"].value:
            startDate = new Date();
            endDate = new Date();
            break;
        case DATE_FILTERS["WEEK"].value:
            startDate = getMonday(new Date());
            endDate = getSunday(new Date());
            break;
        case DATE_FILTERS["MONTH"].value:
            console.log(present);
            startDate = new Date(present.getFullYear(), present.getMonth(), 1);
            endDate = new Date(present.getFullYear(), present.getMonth() + 1, 0);
            console.log(startDate.toLocaleString());
            break;
        case DATE_FILTERS["YEAR"].value:
            startDate = new Date(present.getFullYear(), 0, 1);
            endDate = new Date(present.getFullYear(), 11, 31);
            break;
        case DATE_FILTERS["CUSTOM"].value:
            startDate = dateSpan.startDate;
            endDate = dateSpan.endDate;
            break;
        default:
            startDate = "";
            endDate = "";
    }
    return { startDate, endDate };
};

export const roundOff = (num: number)=>{
    return parseFloat(
        (Math.round((num + Number.EPSILON) * 100) / 100).toFixed(2)
      )
}

export const calculateSubscriptionEndDate = (subscriptionStartDate: Date, subscriptionType: string): Date => {
    // Create a new Date object from the start date to avoid mutation
    const endDate = new Date(subscriptionStartDate);

    if(subscriptionType === SUBSCRIPTION_INTERVAL.YEARLY){
        // Add one year to the billing start date
        endDate.setFullYear(endDate.getFullYear() + 1);
        // Handle cases where the resulting year does not have the same day (e.g., billing on Feb 29 in a leap year)
        if (endDate.getMonth() !== subscriptionStartDate.getMonth()) {
            // If the new year does not have the same month/day (Feb 29th to Feb 28th), set it to the last day of the previous month
            endDate.setMonth(endDate.getMonth() - 1);
            endDate.setDate(0); // This will set the date to the last day of the previous month
        }
    }
    else if(subscriptionType === SUBSCRIPTION_INTERVAL.MONTHLY){
        // Add one month to the billing start date
        endDate.setMonth(endDate.getMonth() + 1);
    }
    else if(subscriptionType === SUBSCRIPTION_INTERVAL.QUARTERLY){
        // Add one month to the billing start date
        endDate.setMonth(endDate.getMonth() + 4);
    }

    // Return the calculated billing cycle end date
    return endDate;
};

// Environment helper: returns 'dev' for dev.prismprocure.ai, otherwise 'prod'
export const getEnvName = (): 'dev' | 'prod' => {
    try {
        const host = (typeof window !== 'undefined' && window.location && window.location.hostname) ? window.location.hostname : '';
        if (host === 'dev.prismprocure.ai') {
            return 'dev';
        }
        return 'prod';
    } catch (e) {
        // Default to prod on any unexpected environment (SSR, tests, etc.)
        return 'prod';
    }
};

export function isValidHSN(hsn: string): boolean {
  var regExp = /^[+-]?\d+(\.\d+)?$/g;
  return (hsn!="" && [2,4,6,8].includes(hsn.trim().length) && regExp.test(hsn) )
}

// Format the amount as Indian Rupee currency with `Intl.NumberFormat`
export const formatAmount = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(amount);
  };